import { GridColDef } from '@mui/x-data-grid';
import {
  addItemsBaseColumnProps,
  GetCampaignItemsColumnsFn,
  useAddItemsBaseColumns,
} from './useAddItemsBaseColumns';
import { dataGridBoldClass } from '@shared/styles';
import { Checkbox, Stack, Tooltip } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { BrandMgSupplierCampaignItem } from '@retail/campaign/types';
import { Info } from '@mui/icons-material';
import { useCampaignTFunction } from '@retail/campaign/i18n';
import { useCallback, useMemo } from 'react';
import { AddCampaignItemsSchema, BrandCampaignItemSchema } from './addItemsSchema';
import { ItemInfoColumnsProps } from './useItemInfoColumns';
import { PercentageInput } from './PercentageInput';
import { PriceInput } from './PriceInput';

interface Props extends ItemInfoColumnsProps {
  showPackageDiscounts: boolean;
}

export const useGetAddBrandCampaignItemsColumns = ({
  externalSourceType,
  showPackageDiscounts,
}: Props) => {
  const baseColumns = useAddItemsBaseColumns({ externalSourceType });
  const t = useCampaignTFunction();

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<AddCampaignItemsSchema<BrandCampaignItemSchema>>();

  const campaignSalesPriceColumn: GridColDef<BrandMgSupplierCampaignItem> = useMemo(() => {
    return {
      ...addItemsBaseColumnProps,
      cellClassName: () => dataGridBoldClass,
      width: 140,
      headerAlign: 'center',
      align: 'center',
      field: 'campaignSalesPrice',
      headerName: t('campaign.columns.campaignSalesPrice.label'),
      renderCell: ({ row }) => {
        const error = errors.items?.[row.mgSupplierItemId]?.campaignSalesPrice;
        const useDiscount = watch(`items.${row.mgSupplierItemId}.useDiscount`);
        return (
          <PriceInput
            error={error?.message}
            fieldName={`items.${row.mgSupplierItemId}.campaignSalesPrice`}
            control={control}
            incVat={true}
            disabled={!!useDiscount}
          />
        );
      },
    };
  }, [control, errors.items, t, watch]);

  const fixedSalesPriceColumn: GridColDef<BrandMgSupplierCampaignItem> = useMemo(
    () => ({
      ...addItemsBaseColumnProps,
      cellClassName: () => dataGridBoldClass,
      width: 180,
      headerAlign: 'center',
      align: 'center',
      field: 'fixedSalesPrice',
      headerName: t('campaign.columns.fixedSalesPrice.label'),
      renderHeader: () => (
        <Stack direction="row" alignItems="center" gap={1}>
          <div>{t('campaign.columns.fixedSalesPrice.label')}</div>
          <Tooltip title={t('campaign.columns.fixedSalesPrice.info')}>
            <Info color="primary" />
          </Tooltip>
        </Stack>
      ),
      renderCell: ({ row }) => {
        return (
          <Controller
            name={`items.${row.mgSupplierItemId}.fixedSalesPrice`}
            control={control}
            render={({ field }) => <Checkbox {...field} checked={field.value} />}
          />
        );
      },
    }),
    [control, t]
  );

  const packageDiscountColumns: GridColDef<BrandMgSupplierCampaignItem>[] = useMemo(
    () => [
      {
        ...addItemsBaseColumnProps,
        cellClassName: () => dataGridBoldClass,
        width: 140,
        headerAlign: 'center',
        align: 'center',
        field: 'secondaryDiscountPercentage',
        headerName: t('campaign.columns.secondaryDiscountPercentage'),
        renderCell: ({ row }) => {
          const error = errors.items?.[row.mgSupplierItemId]?.secondaryDiscountPercentage;
          const currentSalesPrice = watch(`items.${row.mgSupplierItemId}.campaignSalesPrice`);
          return (
            <PercentageInput
              error={error?.message}
              disabled={!currentSalesPrice}
              fieldName={`items.${row.mgSupplierItemId}.secondaryDiscountPercentage`}
              control={control}
            />
          );
        },
      },
      {
        ...addItemsBaseColumnProps,
        cellClassName: () => dataGridBoldClass,
        width: 140,
        headerAlign: 'center',
        align: 'center',
        field: 'tertiaryDiscountPercentage',
        headerName: t('campaign.columns.tertieryDiscountPercentage'),
        renderCell: ({ row }) => {
          const error = errors.items?.[row.mgSupplierItemId]?.tertiaryDiscountPercentage;
          const currentSalesPrice = watch(`items.${row.mgSupplierItemId}.campaignSalesPrice`);
          return (
            <PercentageInput
              error={error?.message}
              disabled={!currentSalesPrice}
              fieldName={`items.${row.mgSupplierItemId}.tertiaryDiscountPercentage`}
              control={control}
            />
          );
        },
      },
    ],
    [control, errors.items, t, watch]
  );

  const getCampaignItemColumns: GetCampaignItemsColumnsFn<BrandMgSupplierCampaignItem> =
    useCallback(
      ({ incVat, deleteItem }) => [
        baseColumns.getItemIdsColumn(),
        baseColumns.getItemNameColumn(),
        baseColumns.getSupplierColumn(),
        baseColumns.getPrimaryPackageUnitColumn(),
        baseColumns.getCampaignUnitColumn(),
        baseColumns.getCampaignPurchasePriceColumn(),
        campaignSalesPriceColumn,
        baseColumns.getSalesCoverageColumn(),
        fixedSalesPriceColumn,
        ...(showPackageDiscounts ? packageDiscountColumns : []),
        baseColumns.getFinfoBasePriceColumn(incVat),
        baseColumns.getFinfoRecPriceColumn(incVat),
        baseColumns.getActionsColumn(deleteItem),
      ],
      [
        baseColumns,
        fixedSalesPriceColumn,
        showPackageDiscounts,
        packageDiscountColumns,
        campaignSalesPriceColumn,
      ]
    );

  return getCampaignItemColumns;
};
